<!--
  @component LogsAggregate — cross-project Logs (scope = all / many).
  tier:   deployments aggregate view

  A unified, newest-first activity stream over the scoped event ledger (the same
  source the single-project Logs screen reads, filtered to the current scope and
  labelled by project). Real data — no fabrication.
-->
<script lang="ts">
  import { useIdentityIO } from "../../accounts/IdentityIO.svelte";
  import { useProvenanceIO } from "../../../../global/io/nodes/deployment/ProvenanceIO.svelte";
  import { useScopeIO } from "./ScopeIO.svelte";
  import { feedEntries, relTime, type FeedTone } from "../../home/home-util.js";
  import { scopeEvents } from "./fleet-util.js";

  const identity = useIdentityIO();
  const provenance = useProvenanceIO();
  const scope = useScopeIO();

  const events = $derived(scopeEvents(provenance.events, scope.projectIds));
  const entries = $derived(feedEntries(events, 100));

  const TONE: Record<FeedTone, string> = { ok: "ok", info: "info", warn: "warn", error: "error" };

  function nameOf(pid: string | null): string | null {
    return pid ? (identity.projects.find((p) => p.id === pid)?.name ?? null) : null;
  }
</script>

<div class="logs">
  <header>
    <h1>Logs</h1>
    <p class="lede">
      Unified activity across {scope.count} project{scope.count === 1 ? "" : "s"} — newest first.
    </p>
  </header>

  {#if entries.length === 0}
    <p class="empty">No activity yet.</p>
  {:else}
    <ul class="stream">
      {#each entries as e (e.id)}
        <li class="line">
          <span class="tone {TONE[e.tone]}" aria-hidden="true"></span>
          <span class="t">{relTime(e.atMs)}</span>
          <span class="label">{e.label}</span>
          {#if nameOf(e.pid)}<span class="proj">{nameOf(e.pid)}</span>{/if}
        </li>
      {/each}
    </ul>
  {/if}
</div>

<style>
  .logs { display: flex; flex-direction: column; gap: var(--space-4); max-width: 1100px; font-family: var(--type-font); }
  header { display: flex; flex-direction: column; gap: var(--space-1); }
  h1 { margin: 0; font-size: 22px; font-weight: 600; letter-spacing: -0.02em; color: var(--text-primary); }
  .lede { margin: 0; font-size: 13.5px; color: var(--text-secondary); }
  .empty { margin: 0; font-size: 13px; color: var(--text-tertiary); }
  .stream {
    list-style: none; margin: 0; padding: var(--space-2) var(--space-4);
    background: var(--surface-sunken); border: 0.5px solid var(--border);
    border-radius: var(--radius-lg, 14px);
    display: flex; flex-direction: column;
    font-family: var(--type-mono); font-size: 12px;
  }
  .line {
    display: flex; align-items: center; gap: var(--space-2);
    padding: 6px 0; border-bottom: 0.5px solid var(--border-subtle);
  }
  .line:last-child { border-bottom: 0; }
  .tone { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; background: var(--text-tertiary); }
  .tone.ok { background: var(--success); } .tone.warn { background: var(--warning); } .tone.error { background: var(--error); } .tone.info { background: var(--accent, #5b8cff); }
  .t { color: var(--text-ghost); flex-shrink: 0; min-width: 56px; }
  .label { color: var(--text-primary); }
  .proj { margin-left: auto; color: var(--text-tertiary); flex-shrink: 0; padding-left: var(--space-3); }
</style>
